/**
 * Gnomux objects.
 *
 * Objects can be read-only or read-write.
 *
 * An example of read-only objects would be GnomuxReadMapping, where the data
 * can be stored and viewed in the application, but not modified. The advantage
 * of read-only objects is that the backing file and database representation do
 * not need to be kept in sync.
 *
 * An example of read-write objects would be GnomuxSequence, where the user can
 * edit sequences and save the result. In these cases, we need to ingest any
 * uploaded files and represent them in the database. The files will not be
 * retained and will instead be reconstituted on demand.
 *
 * Object Types:
 *
 * - GnomuxSequence: A single sequence (nucleotide or amino acid).
 * - GnomuxSequenceCollection: Multiple sequences stored together.
 *
 */

export type GnomuxObjectType =
  | "GnomuxAlignment"
  | "GnomuxSequence"
  | "GnomuxSequenceCollection"
  | "GnomuxTree";

export type GnomuxObjectStatus = "importing" | "error";

type GnomuxObjectBase = {
  /* A UUID for the object. */
  id: string;
  /* When the object was created. */
  createdAt: Date;
  /* A name for the object. */
  name: string;
  /* The ID of the user who owns the object. */
  ownerId: string;
  /* The import status of the object. Undefined when fully imported. */
  status?: GnomuxObjectStatus;
};

export type GnomuxObject = GnomuxObjectBase &
  (
    | { deletedAt: Date; originalParent: string }
    | { deletedAt?: undefined; originalParent?: undefined }
  );

export type GnomuxDirectory = GnomuxObject & {
  parent: string;
};

export type GnomuxFile = GnomuxObject & {
  content: string | string[];
  isLocked: boolean;
  parent: string;
  type: GnomuxObjectType;
};

export const GNOMUX_ROOT_ID = "root" as const;
export const GNOMUX_TRASH_ID = "trash" as const;
